// App Bridge v4 injects a global `shopify` object on window (via the CDN
// script tag in index.html). We use it to fetch a session token per request.
declare global {
  interface Window {
    shopify?: {
      idToken: () => Promise<string>;
    };
  }
}

export interface ApiError {
  error: string;
  message: string;
  // Present only on `reauth_required`: where to send the merchant to reinstall.
  reauth_url?: string;
}

export class ApiRequestError extends Error {
  constructor(public status: number, public body: ApiError) {
    super(body.message);
  }
}

// The shop's stored Shopify credentials can no longer be read, so every call
// that touches Shopify will fail until the merchant reinstalls. Any request can
// be the one that discovers it, and the page that happens to make that request
// is not necessarily the page that should explain it — so it is announced here
// once and rendered by the app shell.
//
// Latched rather than only broadcast: the discovering request usually lands
// before the banner has mounted, and a listener that arrives afterwards still
// needs to know.
let latchedReauthUrl: string | null = null;
const reauthListeners = new Set<(url: string | null) => void>();

export function pendingReauthUrl(): string | null {
  return latchedReauthUrl;
}

export function onReauthRequired(listener: (url: string | null) => void): () => void {
  reauthListeners.add(listener);
  return () => {
    reauthListeners.delete(listener);
  };
}

// Called after a successful reinstall: any `/api/*` response coming back 2xx
// proves the shop is installed and the stored credentials are readable, so a
// banner from a stale earlier session must clear itself. Without this, a
// merchant who reinstalls from outside the banner (Partner Dashboard, App Store
// re-add, direct URL) hits a still-rendered banner because the module-level
// latch has no other way to reset. See rapistoredemo-reconnect-banner root-cause
// analysis.
function clearLatchedReauth(): void {
  if (latchedReauthUrl === null) return;
  latchedReauthUrl = null;
  for (const listener of reauthListeners) listener(null);
}

// Lets a page skip its own error banner for this failure, since the app shell
// is already showing it with the button that fixes it.
export function isReauthError(error: unknown): boolean {
  return error instanceof ApiRequestError && error.body.error === "reauth_required";
}

async function getSessionToken(): Promise<string> {
  if (!window.shopify) {
    throw new Error("App Bridge not initialized (window.shopify missing)");
  }
  return window.shopify.idToken();
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getSessionToken();
  const response = await fetch(`/api${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({ error: "unknown", message: response.statusText }))) as ApiError;
    if (body.error === "reauth_required" && body.reauth_url) {
      latchedReauthUrl = body.reauth_url;
      for (const listener of reauthListeners) listener(body.reauth_url);
    }
    throw new ApiRequestError(response.status, body);
  }

  // 2xx from any /api/* endpoint means the session token verified AND the
  // shops row exists AND the request-scoped handler ran without throwing
  // ShopReauthRequiredError. That is the exact condition a stale banner is
  // claiming to be false, so clear it now.
  clearLatchedReauth();

  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: "DELETE" }),
};
